import { useTranslation } from "react-i18next";
import { formatDateShort } from "../../lib/format";
import type { InterestPhase, Loan } from "../../lib/realestate";

interface Props {
  loan: Loan;
  phases: InterestPhase[];
  onAdd: () => void;
  onEdit: (phase: InterestPhase) => void;
  onDelete: (phase: InterestPhase) => void;
}

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
  });
}

function formatPct(bp: number): string {
  return `${(bp / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`;
}

export function InterestPhaseList({ loan, phases, onAdd, onEdit, onDelete }: Props) {
  const { t } = useTranslation();

  return (
    <section className="space-y-2">
      <header className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          {t("realestate.loan.interest_phase.section_title")}
        </h4>
        <button type="button" className="text-sm px-2 py-1 border rounded" onClick={onAdd}>
          {t("realestate.loan.interest_phase.add")}
        </button>
      </header>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left border-b text-xs text-slate-600">
            <th className="p-1" />
            <th className="p-1">{t("realestate.loan.interest_phase.start_date")}</th>
            <th className="p-1 text-right">{t("realestate.loan.interest_phase.annual_rate")}</th>
            <th className="p-1 text-right">
              {t("realestate.loan.interest_phase.monthly_payment")}
            </th>
            <th className="p-1">{t("realestate.loan.interest_phase.notes")}</th>
            <th className="p-1" />
          </tr>
        </thead>
        <tbody>
          <tr className="border-b">
            <td className="p-1 italic text-slate-600">
              {t("realestate.loan.interest_phase.initial_label")}
            </td>
            <td className="p-1 font-mono text-xs">{formatDateShort(loan.startDate)}</td>
            <td className="p-1 text-right font-mono">{formatPct(loan.annualInterestRateBp)}</td>
            <td className="p-1 text-right font-mono">{formatEur(loan.monthlyPaymentCents)}</td>
            <td className="p-1 text-xs text-slate-500">—</td>
            <td className="p-1" />
          </tr>
          {phases.map((p, idx) => (
            <tr key={p.id} className="border-b">
              <td className="p-1 text-slate-600">
                {t("realestate.loan.interest_phase.phase_index", { n: idx + 1 })}
              </td>
              <td className="p-1 font-mono text-xs">{formatDateShort(p.startDate)}</td>
              <td className="p-1 text-right font-mono">{formatPct(p.annualInterestRateBp)}</td>
              <td className="p-1 text-right font-mono">{formatEur(p.monthlyPaymentCents)}</td>
              <td className="p-1 text-xs text-slate-600">{p.notes ?? "—"}</td>
              <td className="p-1 whitespace-nowrap">
                <button
                  type="button"
                  className="text-xs text-blue-600 underline mr-2"
                  onClick={() => onEdit(p)}
                >
                  {t("realestate.loan.interest_phase.edit")}
                </button>
                <button
                  type="button"
                  className="text-xs text-red-600 underline"
                  onClick={() => onDelete(p)}
                >
                  {t("realestate.loan.interest_phase.delete")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
